import * as XLSX from 'xlsx';

/**
 * Generic Excel Export Utilities
 * Reusable Excel export functionality for the entire application
 */
export class ExcelUtils {
    /**
     * Export data to Excel file
     * @param {Array} data - Array of objects to export
     * @param {Array} headers - Array of column headers
     * @param {Array} columnWidths - Array of column width objects
     * @param {string} sheetName - Name of the worksheet
     * @param {string} filename - Name of the file to download
     */
    static exportToExcel(data, headers, columnWidths, sheetName = 'Sheet1', filename = 'export') {
        try {
            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(data);
            
            // Set headers in first row
            XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
            
            // Set column widths if provided
            if (columnWidths && columnWidths.length > 0) {
                worksheet['!cols'] = columnWidths;
            }
            
            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            
            // Generate file and trigger download
            const excelBuffer = XLSX.write(workbook, { 
                bookType: 'xlsx', 
                type: 'array' 
            });
            
            this.downloadFile(excelBuffer, `${filename}.xlsx`);
            
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            throw new Error('Error al generar el archivo Excel');
        }
    }
    
    /**
     * Format price for display
     * @param {number} amount - Price amount
     * @param {string} currency - Currency code (default: EUR)
     * @param {string} locale - Locale for formatting (default: es-ES)
     * @returns {string} Formatted price
     */
    static formatPrice(amount, currency = 'EUR', locale = 'es-ES') {
        if (amount === 0) return 'Gratis';
        if (!amount || isNaN(amount)) return 'N/A';
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency
        }).format(amount);
    }
    
    /**
     * Format age range for display
     * @param {number} ageFrom - Starting age
     * @param {number} ageTo - Ending age
     * @returns {string} Formatted age range
     */
    static formatAgeRange(ageFrom, ageTo) {
        if (ageFrom === ageTo) {
            return `${ageFrom} años`;
        }
        return `${ageFrom}-${ageTo} años`;
    }
    
    /**
     * Format boolean for display
     * @param {boolean} value - Boolean value
     * @param {string} trueText - Text for true value (default: 'Sí')
     * @param {string} falseText - Text for false value (default: 'No')
     * @returns {string} Formatted boolean
     */
    static formatBoolean(value, trueText = 'Sí', falseText = 'No') {
        return value ? trueText : falseText;
    }
    
    /**
     * Generate timestamp for filename
     * @returns {string} Formatted timestamp
     */
    static generateTimestamp() {
        return new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    }
    
    /**
     * Trigger file download
     * @param {ArrayBuffer} data - File data
     * @param {string} filename - File name
     */
    static downloadFile(data, filename) {
        const blob = new Blob([data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
} 